import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ReviewStatus, AuditStatus, resolveLocalized, type TemplateField, type AiFinding, type FieldSource, type FieldSources, type ReportConfirmations, type DiagnosisCandidate, unconfirmedSections } from "@arna-med/shared";
import { resolveFieldLabel } from "@/lib/reportFields";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle,
  CheckCircle2,
  FileDown,
  Save,
  Loader2,
  Send,
  RotateCcw,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useReport, usePatchReport, useExportReportPdf, useSetDiagnosis, useConfirmSections } from "@/hooks/api/useReports";
import { PrimaryVisitPanel } from "@/components/report/PrimaryVisitPanel";
import { SourceBadge } from "@/components/report/SourceBadge";
import { useSubmitForAudit, useResubmitForAudit } from "@/hooks/api/useAudit";
import { useConsultation } from "@/hooks/api/useConsultations";
import { useTemplate } from "@/hooks/api/useTemplates";

const KZ_SOAP_SECTIONS = [
  { key: "complaints", sectionLabel: "I", i18nKey: "report.kzsoap.complaints" },
  {
    key: "anamnesisDisease",
    sectionLabel: "II",
    i18nKey: "report.kzsoap.anamnesisDisease",
  },
  {
    key: "anamnesisLife",
    sectionLabel: "III",
    i18nKey: "report.kzsoap.anamnesisLife",
  },
  {
    key: "objectiveStatus",
    sectionLabel: "IV",
    i18nKey: "report.kzsoap.objectiveStatus",
  },
  {
    key: "localStatus",
    sectionLabel: "V",
    i18nKey: "report.kzsoap.localStatus",
  },
  { key: "diagnosis", sectionLabel: "VI", i18nKey: "report.kzsoap.diagnosis" },
  { key: "plan", sectionLabel: "VII", i18nKey: "report.kzsoap.plan" },
] as const;

interface ReportPanelProps {
  id: string;
  /**
   * The report is being regenerated and is about to be replaced. Exporting,
   * saving or submitting now would act on the version that is going away.
   */
  isRegenerating?: boolean;
}

export function ReportPanel({ id, isRegenerating = false }: ReportPanelProps) {
  const { t, i18n } = useTranslation();

  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [generalEdits, setGeneralEdits] = useState<Record<string, string>>({});
  const [doctorNotesDraft, setDoctorNotesDraft] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const { data: report, isLoading: reportLoading } = useReport(id);
  const { data: consultation } = useConsultation(id);
  const { data: template } = useTemplate(consultation?.templateId);
  const patchMutation = usePatchReport(id);
  const exportPdfMutation = useExportReportPdf(id);
  const submitMutation = useSubmitForAudit(id);
  const resubmitMutation = useResubmitForAudit(id);
  const setDiagnosisMutation = useSetDiagnosis(id);
  const confirmSectionsMutation = useConfirmSections(id);

  const handleSave = useCallback((afterSave?: () => void) => {
    const mergedSpecialized = { ...(report?.editedData ?? {}), ...edits };
    const mergedGeneral = {
      ...(report?.generalData ?? {}),
      ...(report?.generalEditedData ?? {}),
      ...generalEdits,
    };

    const hasRemainingMissing = (report?.missingFields ?? []).some((key) => {
      const val = mergedSpecialized[key] ?? report?.structuredData?.[key];
      if (typeof val === "object" && val !== null) {
        const v = val as { right?: unknown; left?: unknown };
        return !v.right || !v.left;
      }
      return !val;
    });

    patchMutation.mutate(
      {
        editedData: Object.keys(edits).length ? mergedSpecialized : undefined,
        generalEditedData: Object.keys(generalEdits).length
          ? mergedGeneral
          : undefined,
        reviewStatus: hasRemainingMissing
          ? ReviewStatus.Draft
          : ReviewStatus.Complete,
        doctorNotes: doctorNotesDraft !== null ? doctorNotesDraft : undefined,
      },
      {
        onSuccess: () => {
          setEdits({});
          setGeneralEdits({});
          setDoctorNotesDraft(null);
          setIsDirty(false);
          afterSave?.();
        },
      },
    );
  }, [report, edits, generalEdits, doctorNotesDraft, patchMutation]);

  const handleFieldEdit = (key: string, value: unknown) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleBilateralEdit = (
    key: string,
    side: "right" | "left",
    value: string,
  ) => {
    setEdits((prev) => {
      const existing = (prev[key] ??
        report?.editedData?.[key] ??
        report?.structuredData?.[key]) as
        | { right?: string; left?: string }
        | undefined;
      return { ...prev, [key]: { ...existing, [side]: value } };
    });
    setIsDirty(true);
  };

  const handleGeneralEdit = (key: string, value: string) => {
    setGeneralEdits((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleDoctorNotesEdit = (value: string) => {
    setDoctorNotesDraft(value);
    setIsDirty(true);
  };

  if (reportLoading) return <Spinner />;
  if (!report)
    return (
      <p className="p-4 text-sm text-muted-foreground">Report not found.</p>
    );

  // Prefer the snapshot captured on the report at generation time so template
  // edits don't retroactively change this report; fall back to the live template.
  const snapshotFields = report.templateSnapshot?.fields as
    | TemplateField[]
    | undefined;
  const templateFields =
    snapshotFields && snapshotFields.length > 0
      ? snapshotFields
      : (template?.fields as TemplateField[] | undefined);
  const isGeneralTemplate = template?.isGeneral ?? false;
  const mergedData = {
    ...report.structuredData,
    ...(report.editedData ?? {}),
    ...edits,
  };
  const missingSet = new Set(report.missingFields ?? []);

  // A field is "actively missing" only if it's in missingSet AND still empty locally.
  // This lets the amber indicator clear in real-time as the user types, before saving.
  const isFieldMissing = (key: string): boolean => {
    if (!missingSet.has(key)) return false;
    const val = mergedData[key];
    if (typeof val === "object" && val !== null) {
      const v = val as { right?: unknown; left?: unknown };
      return !v.right || !v.left;
    }
    return !val;
  };
  const hasUnfilledMissing = (report.missingFields ?? []).some((key) =>
    isFieldMissing(key),
  );

  const mergedGeneral = {
    ...(report.generalData ?? {}),
    ...(report.generalEditedData ?? {}),
    ...generalEdits,
  } as Record<string, string | null | undefined>;

  const hasGeneralData = report.generalData != null;
  const hasCustomFields = templateFields && templateFields.length > 0;

  // The primary visit is recognised by the questionnaire, not by the report's
  // contents. This panel is the report the doctor actually opens — it is a tab
  // on the consultation screen, so the decisions the visit asks of them have to
  // be here, not only on the standalone report page.
  const fieldSources = (report.fieldSources ?? {}) as FieldSources;
  const confirmations = (report.confirmations ?? {}) as ReportConfirmations;
  const isPrimaryVisit = template?.isPrimaryVisit === true;
  const diagnosisCandidates = (report.diagnosisCandidates ??
    []) as DiagnosisCandidate[];
  // The same rule the backend enforces on submit, so the screen never offers a
  // button the server will refuse.
  const pendingConfirmation = isPrimaryVisit
    ? unconfirmedSections({ fieldSources, confirmations })
    : [];

  return (
    <div className="space-y-4">
      {/* Status + export */}
      <div className="flex items-center justify-between">
        <div>
          {report.reviewStatus === ReviewStatus.Draft ? (
            <span className="flex items-center gap-1 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
              <AlertTriangle className="h-4 w-4" />
              {t("report.draft")}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
              <CheckCircle2 className="h-4 w-4" />
              {t("report.complete")}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (isDirty) {
              handleSave(() => exportPdfMutation.mutate(i18n.language));
            } else {
              exportPdfMutation.mutate(i18n.language);
            }
          }}
          disabled={exportPdfMutation.isPending || patchMutation.isPending || isRegenerating}
        >
          <FileDown className="h-4 w-4 mr-2" />
          {(exportPdfMutation.isPending || patchMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {t("report.export")}
        </Button>
      </div>

      {hasUnfilledMissing && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p className="font-medium mb-1">{t("report.missingFieldsNote")}</p>
          <p className="text-xs">{t("report.missingFieldsHint")}</p>
        </div>
      )}

      {isPrimaryVisit && (
        <PrimaryVisitPanel
          pending={pendingConfirmation}
          confirmedCount={Object.keys(confirmations).length}
          candidates={diagnosisCandidates}
          data={mergedData}
          missingFields={report.missingFields ?? []}
          language={consultation?.language ?? i18n.language}
          onSetDiagnosis={(payload) => setDiagnosisMutation.mutate(payload)}
          onConfirmSection={(sectionKey) =>
            confirmSectionsMutation.mutate([sectionKey])
          }
          isSettingDiagnosis={setDiagnosisMutation.isPending}
          isConfirming={confirmSectionsMutation.isPending}
        />
      )}

      {/* Revision banner */}
      {report.auditStatus === AuditStatus.Revision && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-2">
          <p className="font-medium flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            {t("audit.revisionBanner")}
          </p>
          {report.auditorNotes && (
            <p className="text-xs bg-red-100 rounded p-2">{report.auditorNotes}</p>
          )}
          <div className="space-y-1.5">
            <Button
              size="sm"
              variant="outline"
              className="border-red-300"
              onClick={() => resubmitMutation.mutate()}
              disabled={resubmitMutation.isPending || isRegenerating || report.reviewStatus !== ReviewStatus.Complete}
            >
              <Send className="h-3 w-3 mr-1" />
              {t("audit.resubmit")}
            </Button>
            {report.reviewStatus !== ReviewStatus.Complete && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {t("audit.resubmitHint")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* AI Findings banner */}
      {(() => {
        const aiFindings = (report.aiFindings as AiFinding[] | null) ?? [];
        const activeFindings = aiFindings.filter((f) => !f.fixed);
        if (!aiFindings.length || report.auditStatus === AuditStatus.Approved) return null;
        // Show the localized field label instead of the raw key in findings.
        const findingLabel = (key: string) =>
          resolveFieldLabel(key, templateFields, i18n.language, t);
        return (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm space-y-2">
            <p className="font-medium text-yellow-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t("audit.aiFindingsTitle")} ({activeFindings.length}{activeFindings.length !== aiFindings.length ? ` / ${aiFindings.length} ${t("audit.aiFindingsTotal")}` : ""})
            </p>
            <div className="space-y-1">
              {aiFindings.map((f, i) => (
                <div key={i} className={`flex items-start gap-2 text-xs rounded p-2 ${f.fixed ? "opacity-50 line-through bg-green-50" : f.severity === "error" ? "bg-red-50 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span><strong>{findingLabel(f.field)}:</strong> {f.issue}{f.fixed ? ` ✓ ${t("audit.aiFixed")}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Submit for audit */}
      {report.auditStatus === AuditStatus.NotSubmitted && (
        <div className="flex justify-end items-center gap-3">
          {pendingConfirmation.length > 0 && (
            <p className="text-xs text-amber-700">
              {t("primaryVisit.submitBlocked")}
            </p>
          )}
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={
              submitMutation.isPending ||
              isRegenerating ||
              isDirty ||
              pendingConfirmation.length > 0
            }
          >
            <Send className="h-4 w-4 mr-2" />
            {t("audit.submitForAudit")}
          </Button>
        </div>
      )}

      {/* Pending audit badge */}
      {report.auditStatus === AuditStatus.PendingAudit && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          {t("audit.pendingAudit")}
        </div>
      )}

      {/* Approved badge */}
      {report.auditStatus === AuditStatus.Approved && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          {t("audit.approved")}
        </div>
      )}

      {/* KZ-SOAP: general template without custom fields (structuredData holds the 7 sections) */}
      {isGeneralTemplate && !hasGeneralData && !hasCustomFields && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-baseline justify-between">
              <CardTitle className="text-base">
                {t("report.kzsoap.title")}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {KZ_SOAP_SECTIONS.map(({ key, sectionLabel, i18nKey }) => {
              const displayValue =
                (edits[key] as string | undefined) ??
                (mergedData[key] as string | null | undefined) ??
                "";
              return (
                <div key={key}>
                  <p className="font-semibold text-sm mb-1.5">
                    <span className="inline-flex items-center justify-center w-7 h-5 text-xs bg-primary/10 text-primary rounded mr-2 font-mono">
                      {sectionLabel}
                    </span>
                    {t(i18nKey)}
                    {isFieldMissing(key) && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">
                        {t("report.missingField")}
                      </span>
                    )}
                  </p>
                  <Textarea
                    value={displayValue}
                    onChange={(e) => handleFieldEdit(key, e.target.value)}
                    placeholder="—"
                    className={`text-sm min-h-[120px] resize-y ${isFieldMissing(key) ? "border-amber-400" : ""}`}
                    rows={4}
                  />
                </div>
              );
            })}
            <div>
              <p className="font-semibold text-sm mb-1.5">{t("report.doctorNotes")}</p>
              <Textarea
                value={doctorNotesDraft !== null ? doctorNotesDraft : (report.doctorNotes ?? "")}
                onChange={(e) => handleDoctorNotesEdit(e.target.value)}
                placeholder={t("report.doctorNotesPlaceholder")}
                className="text-sm min-h-[100px] resize-y"
                rows={4}
              />
            </div>
          </CardContent>
          <CardFooter className="border-t pt-3 pb-3 flex justify-end">
            <Button
              onClick={() => handleSave()}
              disabled={!isDirty || patchMutation.isPending || isRegenerating}
              size="sm"
            >
              <Save className="h-4 w-4 mr-2" />
              {patchMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("common.save")}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* KZ-SOAP General Record (legacy: both general_data + structured_data exist) */}
      {hasGeneralData && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-baseline justify-between">
              <CardTitle className="text-base">
                {t("report.kzsoap.title")}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {KZ_SOAP_SECTIONS.map(({ key, sectionLabel, i18nKey }) => {
              const rawValue = mergedGeneral[key];
              const editVal = generalEdits[key];
              const displayValue = editVal ?? rawValue ?? "";
              return (
                <div key={key}>
                  <p className="font-semibold text-sm mb-1.5">
                    <span className="inline-flex items-center justify-center w-7 h-5 text-xs bg-primary/10 text-primary rounded mr-2 font-mono">
                      {sectionLabel}
                    </span>
                    {t(i18nKey)}
                  </p>
                  <Textarea
                    value={displayValue}
                    onChange={(e) => handleGeneralEdit(key, e.target.value)}
                    placeholder="—"
                    className="text-sm min-h-[120px] resize-y"
                    rows={4}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Specialized Template: render when not general, or when general but has custom fields */}
      {(!isGeneralTemplate || hasCustomFields) && (
        <Card>
          {hasGeneralData && (
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {t("report.specializedTitle")}
              </CardTitle>
            </CardHeader>
          )}
          <CardContent className="pt-6 space-y-5">
            {templateFields
              ? templateFields.map((field) => {
                  const isMissing = isFieldMissing(field.key);
                  const value = mergedData[field.key];
                  const fieldLabel = resolveLocalized(field.label, i18n.language);
                  return (
                    <div
                      key={field.key}
                      className={
                        isMissing
                          ? "border-l-4 border-amber-400 pl-3"
                          : undefined
                      }
                    >
                      <p className="font-semibold text-sm mb-1">
                        {fieldLabel}
                        <SourceBadge
                          source={fieldSources[field.key] as FieldSource | undefined}
                        />
                        {isMissing && (
                          <span className="ml-2 text-xs text-amber-600 font-normal">
                            {t("report.missingField")}
                          </span>
                        )}
                      </p>
                      {field.bilateral ? (
                        <div className="grid grid-cols-2 gap-3">
                          {(["right", "left"] as const).map((side) => {
                            const sideVal = (
                              (value ?? {}) as Record<string, string>
                            )[side];
                            const editVal = (
                              (edits[field.key] ?? {}) as Record<string, string>
                            )[side];
                            return (
                              <div key={side}>
                                <p className="text-xs text-muted-foreground mb-1 capitalize">
                                  {t(`report.${side}`)}
                                </p>
                                <Input
                                  value={editVal ?? sideVal ?? ""}
                                  onChange={(e) =>
                                    handleBilateralEdit(
                                      field.key,
                                      side,
                                      e.target.value,
                                    )
                                  }
                                  placeholder={`${t(`report.${side}`)}...`}
                                  className="text-sm"
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <Textarea
                          value={
                            (edits[field.key] as string | undefined) ??
                            (value as string | null | undefined) ??
                            ""
                          }
                          onChange={(e) =>
                            handleFieldEdit(field.key, e.target.value)
                          }
                          placeholder={`${fieldLabel}...`}
                          className="text-sm min-h-[120px] resize-y"
                          rows={4}
                        />
                      )}
                    </div>
                  );
                })
              : Object.entries(mergedData).map(([key, value]) => (
                  <div key={key}>
                    <p className="font-semibold text-sm capitalize mb-1">
                      {key.replace(/([A-Z])/g, " $1")}
                    </p>
                    <Textarea
                      value={String(value ?? "")}
                      onChange={(e) => handleFieldEdit(key, e.target.value)}
                      className="text-sm min-h-[120px] resize-y"
                      rows={4}
                    />
                  </div>
                ))}
            <div>
              <p className="font-semibold text-sm mb-1">{t("report.doctorNotes")}</p>
              <Textarea
                value={doctorNotesDraft !== null ? doctorNotesDraft : (report.doctorNotes ?? "")}
                onChange={(e) => handleDoctorNotesEdit(e.target.value)}
                placeholder={t("report.doctorNotesPlaceholder")}
                className="text-sm min-h-[100px] resize-y"
                rows={4}
              />
            </div>
          </CardContent>
          <CardFooter className="border-t pt-3 pb-3 flex justify-end">
            <Button
              onClick={() => handleSave()}
              disabled={!isDirty || patchMutation.isPending || isRegenerating}
              size="sm"
            >
              <Save className="h-4 w-4 mr-2" />
              {patchMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {t("common.save")}
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
